import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { Avatar, SingleSelectFilter } from '~/components';
import { useApi, useWorkspace } from '~/contexts';

export default function TeammateFilter({
  placeholder = 'Member',
  name,
  value,
  onChange,
  isActive,
  permission = 'manageTimeAndExpenses',
  withPermissions = true,
}) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      let options = data;
      if (!data) {
        const result = await api.www
          .workspaces(workspace.id)
          .members()
          .options({ isActive, permission, withPermissions });
        options = result.data.filter((o) => o.id !== workspace.member.id);
        setData(options);
      }
      if (q) {
        options = new Fuse(options, { keys: ['name'], threshold: 0.4 }).search(q).map((result) => result.item);
      }
      setOptions(options);
    },
    [workspace.id, workspace.member.id, api, data, isActive, permission, withPermissions],
  );

  const groups = useMemo(() => {
    if (!options) return null;
    const grouped = _(options)
      .orderBy((o) => (o.isActive ? 0 : 1))
      .groupBy((o) => (o.isActive ? 'Active' : 'Inactive'))
      .value();

    if (grouped.Inactive) {
      const inactiveIds = new Set(
        _(grouped.Inactive)
          .orderBy([(o) => o.deactivatedAt == null, (o) => o.deactivatedAt], ['asc', 'desc'])
          .take(100)
          .map('id')
          .value(),
      );

      grouped.Inactive = grouped.Inactive.filter((o) => inactiveIds.has(o.id));
    }

    return grouped;
  }, [options]);

  const handleChange = ({ value }) => {
    onChange({ value: options.find((o) => o.id === value) ?? null });
  };

  return (
    <SingleSelectFilter
      icon="user"
      placeholder={placeholder}
      name={name}
      value={value}
      renderValue={(value) => value.name}
      options={options}
      renderOptions={(opts) => {
        const optionsById = _.keyBy(opts, 'id');
        return Object.keys(groups).map((status) => (
          <SingleSelectFilter.Group key={status} label={status}>
            {groups[status].map((option) => (
              <SingleSelectFilter.Option
                key={option.id}
                inactive={!option.isActive}
                style={{ display: 'flex' }}
                {...optionsById[option.id].props}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar value={option} isCircle hasBackground initialsFontSize=".7rem" size="1.5rem" />
                  <span style={{ marginLeft: '.5rem' }}>{option.name}</span>
                </div>
              </SingleSelectFilter.Option>
            ))}
          </SingleSelectFilter.Group>
        ));
      }}
      isLoading={!data}
      onSearch={handleSearch}
      onChange={handleChange}
    />
  );
}
